import { Box, Stack, Typography, useTheme } from '@mui/material';
import {
  Article as ArticleIcon,
  Lock as LockIcon,
  PrivacyTip as ConsentIcon,
  Security as SecurityIcon,
  Storage as RollupIcon,
  Webhook as WebhookIcon,
  Workspaces as TenantsIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material';
import useSWR from 'swr';
import { PageShell } from '@/layout/PageShell';
import { useAuth } from '@/auth/AuthContext';
import { tenants as tenantsApi } from '@/api/platform';
import { StatusPill } from '@/components/StatusPill';
import { relativeTime } from '@/utils/format';

/**
 * The Platform Owner landing. PR-A version is a navigator card grid —
 * each card links into a section that PR-B fills with real data. The
 * grid is real, not placeholder, so once individual sections light up
 * the dashboard naturally evolves into a "summary tiles" view without
 * a layout rewrite.
 */

interface SectionCard {
  title: string;
  description: string;
  to: string;
  icon: ComponentType<SvgIconProps>;
}

const sections: SectionCard[] = [
  { title: 'Tenants',          description: 'Tenants visible across our collections, with their wiring at a glance.',  to: '/platform/tenants',    icon: TenantsIcon },
  { title: 'Facilities',       description: 'Per-tenant clinic mappings to ABDM HIP/HIU bridges.',                       to: '/platform/facilities', icon: ConsentIcon },
  { title: 'Tenant Security',  description: 'IP allowlist + per-tenant rate limits.',                                    to: '/platform/security',   icon: SecurityIcon },
  { title: 'Audit Viewer',     description: 'Cross-tenant event log + trace timeline.',                                  to: '/platform/audit',      icon: ArticleIcon },
  { title: 'Webhooks',         description: 'Outbound delivery monitor + DLQ replay.',                                   to: '/platform/webhooks',   icon: WebhookIcon },
  { title: 'Metrics Rollup',   description: 'Daily aggregation status, backfill, and read.',                             to: '/platform/rollup',     icon: RollupIcon },
  { title: 'Secrets',          description: 'Webhook secret cipher status and KEK rotation.',                            to: '/platform/secrets',    icon: LockIcon },
];

export function PlatformDashboard() {
  const theme = useTheme();
  const { claims } = useAuth();

  return (
    <PageShell
      kicker="PLATFORM"
      title={`Welcome, ${claims?.sub ?? 'operator'}.`}
      subtitle="Cross-tenant operations live here. Pick a section to drill in."
      width="wide"
    >
      <RecentOnboardings />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 2.5,
        }}
      >
        {sections.map((section, idx) => (
          <Box
            key={section.to}
            component={motion.div}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.28,
              ease: [0.2, 0, 0, 1],
              delay: idx * 0.03,
            }}
          >
            <RouterLink to={section.to} style={{ textDecoration: 'none' }}>
              <Box
                sx={{
                  bgcolor: theme.palette.surface.card,
                  border: `1px solid ${theme.palette.border.subtle}`,
                  borderRadius: theme.radius.md + 'px',
                  p: 3,
                  transition: `border-color ${theme.motion.durations.fast}ms ${theme.motion.easings.standard}, box-shadow ${theme.motion.durations.fast}ms ${theme.motion.easings.standard}, transform ${theme.motion.durations.fast}ms ${theme.motion.easings.standard}`,
                  '&:hover': {
                    borderColor: theme.palette.brand.main,
                    boxShadow: theme.elevation.raised,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Stack spacing={1.75}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: theme.radius.sm + 'px',
                      bgcolor: theme.palette.brand.lighter,
                      color: theme.palette.brand.dark,
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <section.icon sx={{ fontSize: 20 }} />
                  </Box>
                  <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="h3" sx={{ fontWeight: 600, fontSize: 16 }}>
                        {section.title}
                      </Typography>
                      <Box
                        sx={{
                          fontSize: 9,
                          fontWeight: 600,
                          letterSpacing: 0.5,
                          color: theme.palette.status.success,
                          bgcolor: theme.palette.status.successBg,
                          borderRadius: theme.radius.xs + 'px',
                          px: 0.75,
                          py: 0.25,
                        }}
                      >
                        LIVE
                      </Box>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
                      {section.description}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            </RouterLink>
          </Box>
        ))}
      </Box>
    </PageShell>
  );
}

function RecentOnboardings() {
  const theme = useTheme();
  const { data, isLoading, error } = useSWR(
    'recent-onboardings',
    () => tenantsApi.recent(10),
    { refreshInterval: 60_000 },
  );

  if (error) return null;

  return (
    <Box
      sx={{
        bgcolor: theme.palette.surface.card,
        border: `1px solid ${theme.palette.border.subtle}`,
        borderRadius: theme.radius.md + 'px',
        p: 3,
        mb: 3,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 2 }}>
        <Typography variant="overline" color="text.tertiary">RECENT ONBOARDINGS</Typography>
        <RouterLink to="/platform/tenants" style={{ textDecoration: 'none' }}>
          <Typography variant="caption" color="brand.main">View all →</Typography>
        </RouterLink>
      </Stack>
      {isLoading && (
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      )}
      {!isLoading && (data?.length ?? 0) === 0 && (
        <Typography variant="body2" color="text.secondary">No tenants yet.</Typography>
      )}
      {!isLoading && (data?.length ?? 0) > 0 && (
        <Stack spacing={1}>
          {data!.map((t) => (
            <Stack
              key={t.tenantId}
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                p: 1.5,
                borderRadius: theme.radius.sm + 'px',
                bgcolor: theme.palette.surface.sunken,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} noWrap>{t.organizationName}</Typography>
                <Typography variant="caption" color="text.tertiary" noWrap sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {t.tenantId} · {t.organizationType ?? '—'}
                </Typography>
              </Box>
              <StatusPill tone={t.environment === 'PRODUCTION' ? 'success' : 'info'}>
                {t.environment ?? 'SANDBOX'}
              </StatusPill>
              <Typography variant="caption" color="text.secondary" sx={{ width: 90 }}>
                {t.integrationMode ?? 'PLATFORM'}
              </Typography>
              <Typography variant="caption" color="text.tertiary" sx={{ width: 90, textAlign: 'right' }}>
                {relativeTime(t.createdAt)}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}
